import React, { useEffect, useState, useCallback } from 'react';
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardContent } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Database, RefreshCw, HardDrive, GitBranch, Activity, Users, ShieldCheck, Archive } from 'lucide-react';
import apiClient from '@/lib/api';
import type { RedisHealthResponse, RedisHealthSnapshot } from '@easyflow/shared';

const POLL_INTERVAL_MS = 30000;

interface MetricRowProps {
  label: string;
  value: string;
  hint?: string;
  tone?: 'ok' | 'warn' | 'crit' | 'neutral';
}

const toneClass: Record<NonNullable<MetricRowProps['tone']>, string> = {
  ok: 'text-emerald-400',
  warn: 'text-amber-400',
  crit: 'text-rose-500',
  neutral: 'text-slate-200',
};

function MetricRow({ label, value, hint, tone = 'neutral' }: MetricRowProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-muted-foreground/70">{label}</span>
      <span className={`font-mono font-medium ${toneClass[tone]}`} title={hint}>
        {value}
      </span>
    </div>
  );
}

function formatUptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}

function isSnapshot(r: RedisHealthResponse | null): r is RedisHealthSnapshot {
  return r !== null && r.ok === true;
}

interface RedisHealthWidgetProps {
  contentMaxHeightClass?: string;
}

export function RedisHealthWidget({ contentMaxHeightClass }: RedisHealthWidgetProps = {}): React.ReactElement {
  const [snap, setSnap] = useState<RedisHealthResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchHealth = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await apiClient.get<RedisHealthResponse>('/health/redis');
      if (res.success && res.data) {
        setSnap(res.data);
        setErrorMsg(null);
      } else {
        setSnap(null);
        setErrorMsg(res.error ?? 'Risposta non valida');
      }
      setLastUpdate(new Date());
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore sconosciuto';
      setSnap(null);
      setErrorMsg(msg);
      setLastUpdate(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchHealth();
    const t = setInterval(() => {
      void fetchHealth();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchHealth]);

  const ok = isSnapshot(snap);
  const statusBadge = ok
    ? { label: 'Healthy', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' }
    : { label: 'Unreachable', cls: 'bg-rose-500/20 text-rose-300 border-rose-500/40' };

  // Task #1604 — status precomputato lato backend (RedisStatus → MetricRow tone).
  const statusToTone = (s: 'green' | 'yellow' | 'red' | undefined): MetricRowProps['tone'] => {
    if (s === 'red') return 'crit';
    if (s === 'yellow') return 'warn';
    return 'ok';
  };
  const fmtAge = (sec: number | null): string => {
    if (sec === null) return 'mai osservato';
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
    return `${Math.floor(sec / 86400)}g`;
  };
  // Task #1629 — quota memoria precomputata dal backend.
  const memUsedPct = ok ? snap.memory.used_pct : 0;
  const memTone: MetricRowProps['tone'] = ok ? statusToTone(snap.memory.used_pct_status) : 'neutral';
  const fmtHours = (h: number | null): string => {
    if (h === null) return 'mai osservato';
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}g`;
  };
  const fmtBytes = (n: number): string => {
    if (n <= 0) return '0B';
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)}GB`;
  };

  return (
    <GlassCard className="h-full flex flex-col">
      <GlassCardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <GlassCardTitle className="flex items-center gap-2 text-base">
          <Database className="h-4 w-4 text-cyan-400" />
          Redis Cluster
          <Badge variant="outline" className={`ml-2 ${statusBadge.cls}`}>
            {statusBadge.label}
          </Badge>
        </GlassCardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void fetchHealth()}
          disabled={loading}
          aria-label="Refresh Redis health"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </GlassCardHeader>
      <GlassCardContent className={`space-y-4 flex-1${contentMaxHeightClass ? ` ${contentMaxHeightClass}` : ''}`}>
        {!ok ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-300">
            {errorMsg ?? snap?.error ?? 'In attesa primo poll...'}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="font-mono">{snap.endpoint} · db{snap.db}</span>
              <span>v{snap.server.version} · {formatUptime(snap.server.uptime_seconds)} · {snap.latency_ms}ms</span>
            </div>

            <section className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <GitBranch className="h-3 w-3" /> Replication
              </div>
              <MetricRow label="Role" value={snap.replication.role} tone={snap.replication.role === 'master' ? 'ok' : 'neutral'} />
              <MetricRow label="Connected slaves" value={String(snap.replication.connected_slaves)} />
              {snap.replication.master_link_status !== null && (
                <MetricRow
                  label="Master link"
                  value={snap.replication.master_link_status}
                  tone={snap.replication.master_link_status === 'up' ? 'ok' : 'crit'}
                />
              )}
            </section>

            <section className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <HardDrive className="h-3 w-3" /> Memory
              </div>
              <MetricRow
                label="Used"
                value={
                  snap.memory.maxmemory_bytes > 0
                    ? `${snap.memory.used_human} / ${snap.memory.maxmemory_human} (${memUsedPct.toFixed(1)}%)`
                    : `${snap.memory.used_human} / unlimited`
                }
                tone={memTone}
                hint={
                  snap.memory.maxmemory_bytes > 0
                    ? 'Soglie: yellow ≥70%, red ≥85%'
                    : 'maxmemory=0 (illimitato): semaforo sempre green'
                }
              />
              {snap.memory.maxmemory_bytes > 0 && (
                <div className="h-1.5 rounded-full bg-slate-800/60 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      memTone === 'crit'
                        ? 'bg-rose-500'
                        : memTone === 'warn'
                          ? 'bg-amber-400'
                          : 'bg-emerald-400'
                    }`}
                    style={{ width: `${Math.min(100, memUsedPct).toFixed(1)}%` }}
                  />
                </div>
              )}
              <MetricRow
                label="Fragmentation"
                value={snap.memory.fragmentation_ratio.toFixed(2)}
                tone={statusToTone(snap.memory.fragmentation_status)}
                hint={`Status: ${snap.memory.fragmentation_status} (rilevante solo >100MB used)`}
              />
            </section>

            <section className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <HardDrive className="h-3 w-3" /> Persistence
              </div>
              <MetricRow
                label="AOF"
                value={snap.persistence.aof_enabled ? `enabled (${snap.persistence.aof_last_status})` : 'disabled'}
                tone={snap.persistence.aof_enabled && snap.persistence.aof_last_status === 'ok' ? 'ok' : 'warn'}
              />
              {snap.persistence.aof_enabled && (
                <>
                  <MetricRow
                    label="AOF size / base"
                    value={`${snap.persistence.aof_size_human} / ${fmtBytes(snap.persistence.aof_base_bytes)}`}
                  />
                  <MetricRow
                    label="AOF growth ratio"
                    value={snap.persistence.aof_growth_ratio.toFixed(2)}
                    tone={statusToTone(snap.persistence.aof_growth_status)}
                    hint="Soglie status conservative: yellow >50, red >100 (default rewrite Redis a 2x)"
                  />
                  <MetricRow
                    label="AOF last rewrite age"
                    value={fmtAge(snap.persistence.aof_last_rewrite_age_sec)}
                    tone={statusToTone(snap.persistence.aof_last_rewrite_age_status)}
                    hint="Età dall'ultimo BGREWRITEAOF osservato dal monitor (yellow >7gg, red >14gg)"
                  />
                </>
              )}
              <MetricRow
                label="RDB last bgsave"
                value={snap.persistence.rdb_last_bgsave_status}
                tone={snap.persistence.rdb_last_bgsave_status === 'ok' ? 'ok' : 'warn'}
              />
            </section>

            <section className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <Users className="h-3 w-3" /> Clients
              </div>
              <MetricRow
                label="Connected / max"
                value={`${snap.clients.connected} / ${snap.clients.max}`}
              />
              <MetricRow label="Blocked" value={String(snap.clients.blocked)} />
            </section>

            <section className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <Activity className="h-3 w-3" /> Ops
              </div>
              <MetricRow label="Ops/sec" value={String(snap.ops.instantaneous_ops_per_sec)} />
              <MetricRow
                label="Hit ratio"
                value={
                  snap.ops.keyspace_hits + snap.ops.keyspace_misses > 0
                    ? `${((snap.ops.keyspace_hits / (snap.ops.keyspace_hits + snap.ops.keyspace_misses)) * 100).toFixed(1)}%`
                    : 'n/a'
                }
              />
              <MetricRow
                label="Rejected conn"
                value={String(snap.ops.rejected_connections)}
                tone={snap.ops.rejected_connections > 0 ? 'crit' : 'ok'}
              />
            </section>

            <section className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <Archive className="h-3 w-3" /> Backup RDB (Task #1629)
              </div>
              <MetricRow
                label="Ultimo backup"
                value={
                  snap.ops_backup.rdb.last_status === 'unknown'
                    ? 'mai osservato'
                    : `${fmtHours(snap.ops_backup.rdb.age_hours)} (${snap.ops_backup.rdb.last_status})`
                }
                tone={statusToTone(snap.ops_backup.rdb.status)}
                hint={
                  snap.ops_backup.rdb.last_run_at
                    ? `Run: ${snap.ops_backup.rdb.last_run_at}`
                    : 'Vedi deployment/redis/redis-backup-rdb.sh su REDIS-1'
                }
              />
              <MetricRow
                label="Smoke restore"
                value={
                  snap.ops_backup.rdb.smoke_status === 'ok'
                    ? 'ok (per-DB DBSIZE + sentinels)'
                    : snap.ops_backup.rdb.smoke_status === 'failed'
                      ? 'FAILED'
                      : 'mai osservato'
                }
                tone={
                  snap.ops_backup.rdb.smoke_status === 'ok'
                    ? 'ok'
                    : snap.ops_backup.rdb.smoke_status === 'failed'
                      ? 'crit'
                      : 'warn'
                }
                hint="Job log separato: redis_backup_smoke"
              />
            </section>

            <section className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <ShieldCheck className="h-3 w-3" /> ACL snapshot (Task #1629)
              </div>
              <MetricRow
                label="Ultimo snapshot"
                value={
                  snap.ops_backup.acl.last_status === 'unknown'
                    ? 'mai osservato'
                    : `${fmtHours(snap.ops_backup.acl.age_hours)} (${snap.ops_backup.acl.last_status})`
                }
                tone={statusToTone(snap.ops_backup.acl.status)}
                hint={
                  snap.ops_backup.acl.last_run_at
                    ? `Run: ${snap.ops_backup.acl.last_run_at}`
                    : 'Vedi deployment/redis/redis-acl-snapshot.sh su REDIS-1'
                }
              />
            </section>
          </>
        )}
        {lastUpdate && (
          <div className="text-xs text-muted-foreground text-right">
            Aggiornato {lastUpdate.toLocaleTimeString('it-IT')}
          </div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
